import { create } from 'zustand'
import { DEFAULT_BUDGET, DEMO_OWNER, DEMO_TEAM, demoStatsFor, ICampaignStats, IFilter, IPerson, IStarter, LEAD_AREAS, STOP_SUGGESTIONS, TStopGroup, VOICES } from './data'

export type TAuth = 'google' | 'microsoft' | 'email'

/** The screens of the setup, in order. Calendar is asked only after an email sign-up. */
export type TStep = 'connect' | 'you' | 'team' | 'calendar' | 'budget' | 'payment' | 'area' | 'number' | 'stops-working' | 'stops-done' | 'stops-never' | 'business'

export interface ICampaignDraft {
   name: string
   text: string
   filters: IFilter[]
   leads: number
   voiceId: string
   assistantName: string
   routing: 'round-robin' | 'jump-ball' | 'me'
   /** Who receives transferred leads: the owner's id, teammate ids, invited emails. All by default. */
   agents: string[]
}

export interface ILaunched extends ICampaignDraft {
   id: string
   areaCodes: string[]
   launchedAt: number
   stats: ICampaignStats
   status: 'live' | 'paused'
}

interface IFirstRun {
   auth: TAuth | null
   email: string
   step: TStep
   direction: 1 | -1
   fubConnected: boolean
   owner: IPerson
   team: IPerson[]
   selectedTeam: string[]
   invited: string[]
   calendar: 'google' | 'microsoft' | null
   budget: number
   paid: boolean
   areaCodes: string[]
   stops: Record<TStopGroup, string[]>
   business: Record<string, string>
   businessSent: boolean
   draft: ICampaignDraft | null
   launched: ILaunched[]
   setupDone: boolean

   signIn: (auth: TAuth, email?: string) => void
   steps: () => TStep[]
   go: (step: TStep) => void
   next: () => void
   back: () => void
   set: <K extends keyof IFirstRun>(key: K, value: IFirstRun[K]) => void
   toggleTeam: (id: string) => void
   invite: (email: string) => void
   setStops: (group: TStopGroup, ids: string[]) => void
   setBusiness: (key: string, value: string) => void
   startDraft: (from: { text?: string; starter?: IStarter }) => void
   updateDraft: (patch: Partial<ICampaignDraft>) => void
   launch: () => ILaunched | null
   toggleCampaign: (id: string) => void
   reset: () => void
}

const initial = () => ({
   auth: null as TAuth | null,
   email: '',
   step: 'connect' as TStep,
   direction: 1 as 1 | -1,
   fubConnected: false,
   owner: DEMO_OWNER,
   team: DEMO_TEAM,
   selectedTeam: [] as string[],
   invited: [] as string[],
   calendar: null as 'google' | 'microsoft' | null,
   budget: DEFAULT_BUDGET,
   paid: false,
   areaCodes: [LEAD_AREAS[0].code],
   stops: { working: [...STOP_SUGGESTIONS.working], done: [...STOP_SUGGESTIONS.done], never: [...STOP_SUGGESTIONS.never] } as Record<TStopGroup, string[]>,
   business: {} as Record<string, string>,
   businessSent: false,
   draft: null as ICampaignDraft | null,
   launched: [] as ILaunched[],
   setupDone: false,
})

export const useFirstRun = create<IFirstRun>((set, get) => ({
   ...initial(),
   signIn: (auth, email) =>
      set({
         auth,
         email: email || (auth === 'email' ? '' : DEMO_OWNER.email),
         calendar: auth === 'google' ? 'google' : auth === 'microsoft' ? 'microsoft' : null,
         step: 'connect',
         direction: 1,
      }),
   steps: () => {
      const all: TStep[] = ['connect', 'you', 'team', 'calendar', 'budget', 'payment', 'area', 'number', 'stops-working', 'stops-done', 'stops-never', 'business']
      return get().auth === 'email' ? all : all.filter((s) => s !== 'calendar')
   },
   go: (step) => {
      const order = get().steps()
      set({ step, direction: order.indexOf(step) >= order.indexOf(get().step) ? 1 : -1 })
   },
   next: () => {
      const order = get().steps()
      const i = order.indexOf(get().step)
      if (i < order.length - 1) set({ step: order[i + 1], direction: 1 })
   },
   back: () => {
      const order = get().steps()
      const i = order.indexOf(get().step)
      if (i > 0) set({ step: order[i - 1], direction: -1 })
   },
   set: (key, value) => set({ [key]: value } as Pick<IFirstRun, typeof key>),
   toggleTeam: (id) => set((s) => ({ selectedTeam: s.selectedTeam.includes(id) ? s.selectedTeam.filter((x) => x !== id) : [...s.selectedTeam, id] })),
   invite: (email) => set((s) => (s.invited.includes(email) ? {} : { invited: [...s.invited, email] })),
   setStops: (group, ids) => set((s) => ({ stops: { ...s.stops, [group]: ids } })),
   setBusiness: (key, value) => set((s) => ({ business: { ...s.business, [key]: value } })),
   startDraft: ({ text, starter }) =>
      set((s) => ({
         draft: {
            name: starter ? starter.title : text || 'New campaign',
            text: text || '',
            filters: starter ? starter.filters : [],
            leads: starter ? starter.leads : 0,
            voiceId: s.draft?.voiceId || VOICES[0].id,
            assistantName: s.draft?.assistantName || VOICES[0].name,
            routing: s.draft?.routing || (seatCount(s) > 1 ? 'round-robin' : 'me'),
            agents: s.draft?.agents || [s.owner.id, ...s.selectedTeam, ...s.invited],
         },
      })),
   updateDraft: (patch) => set((s) => (s.draft ? { draft: { ...s.draft, ...patch } } : {})),
   launch: () => {
      const d = get().draft
      if (!d) return null
      const launched: ILaunched = { ...d, id: 'c' + Date.now(), areaCodes: get().areaCodes, launchedAt: Date.now(), stats: demoStatsFor(d.leads, get().launched.length + 1), status: 'live' }
      set((s) => ({ launched: [launched, ...s.launched], draft: null }))
      return launched
   },
   toggleCampaign: (id) => set((s) => ({ launched: s.launched.map((c) => (c.id === id ? { ...c, status: c.status === 'live' ? 'paused' : 'live' } : c)) })),
   reset: () => set(initial()),
}))

/** Seats = you + selected teammates + invited. */
export const seatCount = (s: Pick<IFirstRun, 'selectedTeam' | 'invited'>) => 1 + s.selectedTeam.length + s.invited.length
